import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
import TableChart, { ColDefs } from 'components/visualization/Table'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import messageUtil from 'utils/messageUtil'
import { useDisplayTheme } from 'hooks/useUiState'
import { Theme } from 'features/ui/types'
import { TableSortOption } from 'enums'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import {
  getSortingConfigByTableSortOption,
  convertToTableData,
  getColorSetting,
  formatPercentShare,
  getNumberFormatter,
  getLanguageLabel
} from './utils'

import { LanguageInsight, LanguageWidgetsType } from '../../types'

interface TableProps {
  widgetId: number
}

const Table = ({ widgetId }: TableProps) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()
  const widgetSetting = useDashboardWidgetSetting<LanguageWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)

  const rowData = useMemo(() => convertToTableData(widgetData?.data), [
    widgetData
  ]) as LanguageInsight[]

  const { countType, tableSortOption } = widgetSetting

  const colDefs: ColDefs<LanguageInsight> = [
    {
      field: 'name',
      headerText: t('language'),
      cellProps: {
        width: '33%',
        textAlign: 'left'
      },
      customCellRender: (cellValue) => getLanguageLabel(cellValue!),
      defaultOrder: TableSortOption.ASCEND,
      comparator: (rowA: LanguageInsight, rowB: LanguageInsight) =>
        getLanguageLabel(rowA.name!) > getLanguageLabel(rowB.name!) ? 1 : -1
    },
    {
      field: 'value',
      headerText: messageUtil.countType(countType!),
      headerProps: {
        textAlign: 'left'
      },
      cellProps: {
        width: '33%',
        textAlign: 'left'
      },
      comparator: (rowA: LanguageInsight, rowB: LanguageInsight) =>
        rowA.value! > rowB.value! ? 1 : -1,
      children: [
        {
          customCellRender: (cellValue: any) => <>{getNumberFormatter().format(cellValue!)}</>,
          cellProps: {
            colSidePaddings: { right: '8px' },
            textAlign: 'right',
            fitWidth: true
          }
        },
        {
          customCellRender: (_, { row }) => (
            <SimpleBar
              progress={row.share!}
              colorSetting={getColorSetting(widgetSetting.countType!)}
              mode={theme}
            />
          ),
          cellProps: {
            colSidePaddings: { left: '0px' },
            textAlign: 'left'
          }
        }
      ],
      defaultOrder: TableSortOption.DESCEND
    },
    {
      field: 'share',
      headerText: t('share'),
      headerProps: {
        textAlign: 'right'
      },
      cellProps: {
        width: '33%',
        textAlign: 'right'
      },
      customCellRender: (cellValue) => formatPercentShare(cellValue!),
      comparator: (rowA: LanguageInsight, rowB: LanguageInsight) =>
        rowA.share! > rowB.share! ? 1 : -1,
      defaultOrder: TableSortOption.DESCEND
    }
  ]

  return (
    <TableChart
      configs={{
        colSortingConfigs: [getSortingConfigByTableSortOption(tableSortOption)],
        keyField: 'name'
      }}
      rowData={rowData}
      colDefs={colDefs}
      isDisplayFoot={false}
    />
  )
}

export default Table
